import React, { useEffect, useState } from 'react'
import { usersHeaderStyles } from './users-header.styles'
import { TextInput, ThemeButton } from '../../../../ui'
import {
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Typography,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import FilterListIcon from '@material-ui/icons/FilterList'
import GetAppIcon from '@material-ui/icons/GetApp'
import { useIntl } from 'react-intl'
import { FilterInput } from '../../../../ui/filter-input'
import { FilterDateRangeComponent } from '../../../../ui/filter-date-range'
import { useUsersContext } from '../../contexts'

const initialFiltersState = {
  _search: '',
  estado: '',
  rol: '',
  fecha_creacion_start: null,
  fecha_creacion_end: null,
  fecha_modificacion_start: null,
  fecha_modificacion_end: null,
}

// Valores permitidos por el contrato definitivo de las lambdas `list-users` /
// `users-export-request` (ver dto.py: status/role) para los desplegables de
// filtro. Se mantienen fijos porque los query params del backend validan
// exactamente estos valores.
const ESTADOS_USUARIO = [
  'CONFIRMED',
  'UNCONFIRMED',
  'RESET_REQUIRED',
  'FORCE_CHANGE_PASSWORD',
  'DISABLED',
].map((estado) => ({ key: estado, value: estado }))
const ROLES_USUARIO = ['ATR', 'ATR+', 'CLIENT'].map((rol) => ({ key: rol, value: rol }))

export const UsersHeaderComponent = () => {
  const { formatMessage } = useIntl()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [formState, setFormState] = useState(initialFiltersState)
  const [confirmExportOpen, setConfirmExportOpen] = useState(false)
  const [exportSuccessOpen, setExportSuccessOpen] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)

  const { getUsersTable, requestUsersExport } = useUsersContext()

  const handleChangeForm = (event) => {
    setFormState({ ...formState, [event.target.name]: event.target.value })
  }

  useEffect(() => {
    getUsersTable(formState)
  }, [formState]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitForm = (event) => {
    event.preventDefault()
    getUsersTable(formState)
  }

  const filterCleanup = () => {
    setFormState(initialFiltersState)
  }

  const handleToggleFilters = () => {
    setFiltersOpen(!filtersOpen)
  }

  const buildExportParams = () => {
    const params = {}
    if (formState._search) params.search = formState._search
    if (formState.estado) params.status = formState.estado
    if (formState.rol) params.role = formState.rol
    if (formState.fecha_creacion_start)
      params.created_from = formState.fecha_creacion_start.toISOString()
    if (formState.fecha_creacion_end) params.created_to = formState.fecha_creacion_end.toISOString()
    if (formState.fecha_modificacion_start)
      params.modified_from = formState.fecha_modificacion_start.toISOString()
    if (formState.fecha_modificacion_end)
      params.modified_to = formState.fecha_modificacion_end.toISOString()
    return params
  }

  const handleConfirmExport = () => {
    setExportLoading(true)
    requestUsersExport(buildExportParams())
      .then(() => {
        setConfirmExportOpen(false)
        setExportSuccessOpen(true)
      })
      .catch(() => {
        setConfirmExportOpen(false)
      })
      .finally(() => {
        setExportLoading(false)
      })
  }

  return (
    <header style={usersHeaderStyles.headerContainer}>
      <div style={usersHeaderStyles.searchContainer}>
        <TextInput
          spaceless="true"
          name="_search"
          placeholder={formatMessage({
            id: 'pages.users.search.placeholder',
            defaultMessage: 'Buscar usuario',
          })}
          value={formState['_search']}
          onChange={handleChangeForm}
          onBlur={handleSubmitForm}
          onKeyDown={(e) => e.keyCode === 13 && handleSubmitForm(e)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          inlineStyle={{ width: '100%' }}
          debounce={true}
          debounceTimeout={500}
        />
        <ThemeButton
          color="primary"
          onClick={filterCleanup}
          style={{ minWidth: 'max-content', margin: '10px 0' }}
        >
          {formatMessage({ id: 'filters.clear.button' })}
        </ThemeButton>
        <ThemeButton
          variant="outlined"
          endIcon={<FilterListIcon />}
          onClick={handleToggleFilters}
          style={{ margin: '10px 0' }}
        >
          {formatMessage({ id: 'filters.toggle.button' })}
        </ThemeButton>
        <ThemeButton
          color="primary"
          variant="outlined"
          endIcon={<GetAppIcon />}
          onClick={() => setConfirmExportOpen(true)}
          style={{ minWidth: 'max-content', margin: '10px 0' }}
        >
          {formatMessage({ id: 'pages.users.export.button', defaultMessage: 'Exportar' })}
        </ThemeButton>
      </div>
      <Collapse in={filtersOpen} style={usersHeaderStyles.collapseContainer}>
        <Box style={usersHeaderStyles.filtersContainer}>
          <FilterInput
            name={'estado'}
            values={ESTADOS_USUARIO}
            value={formState['estado']}
            onChange={handleChangeForm}
            label={formatMessage({
              id: 'pages.users.table.columns.estado.title',
              defaultMessage: 'Estado',
            })}
          />
          <FilterInput
            name={'rol'}
            values={ROLES_USUARIO}
            value={formState['rol']}
            onChange={handleChangeForm}
            label={formatMessage({ id: 'pages.users.filters.rol.label', defaultMessage: 'Rol' })}
          />
          <FilterDateRangeComponent
            name={'fecha_creacion'}
            label={formatMessage({
              id: 'pages.users.filters.fecha_creacion.label',
              defaultMessage: 'Fecha creación',
            })}
            startValue={formState.fecha_creacion_start}
            endValue={formState.fecha_creacion_end}
            onChange={handleChangeForm}
          />
          <FilterDateRangeComponent
            name={'fecha_modificacion'}
            label={formatMessage({
              id: 'pages.users.filters.fecha_modificacion.label',
              defaultMessage: 'Última modificación',
            })}
            startValue={formState.fecha_modificacion_start}
            endValue={formState.fecha_modificacion_end}
            onChange={handleChangeForm}
          />
        </Box>
      </Collapse>

      <Dialog open={confirmExportOpen} maxWidth="sm" fullWidth>
        <Box p={2}>
          <DialogTitle>
            <Typography variant="h6" color="primary">
              {formatMessage({
                id: 'pages.users.export.confirm.title',
                defaultMessage: 'Exportar usuarios',
              })}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {formatMessage({
                id: 'pages.users.export.confirm.description',
                defaultMessage:
                  'Se generará un fichero con los usuarios filtrados. Recibirás un email cuando esté disponible para su descarga. El enlace tendrá validez de 24 horas o un solo uso.',
              })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <ThemeButton onClick={() => setConfirmExportOpen(false)} disabled={exportLoading}>
              {formatMessage({ id: 'global.cancel' })}
            </ThemeButton>
            <ThemeButton color="primary" onClick={handleConfirmExport} loading={exportLoading}>
              {formatMessage({ id: 'pages.users.export.confirm.yes', defaultMessage: 'Confirmar' })}
            </ThemeButton>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={exportSuccessOpen} maxWidth="sm" fullWidth>
        <Box p={2}>
          <DialogTitle>
            <Typography variant="h6" color="primary">
              {formatMessage({
                id: 'pages.users.export.success.title',
                defaultMessage: 'Solicitud enviada',
              })}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {formatMessage({
                id: 'pages.users.export.success.description',
                defaultMessage:
                  'La solicitud de exportación ha sido procesada. Recibirás un email cuando el fichero esté listo para descargar. El acceso estará disponible durante 24 horas o hasta un solo uso.',
              })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <ThemeButton color="primary" onClick={() => setExportSuccessOpen(false)}>
              {formatMessage({ id: 'global.accept' })}
            </ThemeButton>
          </DialogActions>
        </Box>
      </Dialog>
    </header>
  )
}
